import Icon from "@/components/Icon/Icon";
import { VStack } from "@/components/Layout";
import { Text } from "@/components/Typography/Text";
import { cn } from "@/lib/cn";

interface FilePreviewContentProps {
  imageUrl: string | null;
  filename: string;
  isLoading: boolean;
  error: string | null;
  onImageError: () => void;
}

/**
 * ファイルプレビューの表示内容を担当するコンポーネント
 */
export function FilePreviewContent({
  imageUrl,
  filename,
  isLoading,
  error,
  onImageError,
}: FilePreviewContentProps) {
  // ローディング表示
  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center h-96")}>
        <VStack className="items-center gap-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          <Text variant="labelSmall" className="text-neutral-muted-on-fill">
            読み込み中...
          </Text>
        </VStack>
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <div className={cn("flex items-center justify-center h-96")}>
        <VStack className="items-center gap-4">
          <Icon iconName="AlertCircle" className="w-12 h-12 text-destructive" />
          <VStack className="items-center gap-2">
            <Text variant="labelSemiLarge" className="text-destructive">
              エラーが発生しました
            </Text>
            <Text
              variant="pSmall"
              className="text-neutral-muted-on-fill text-center"
            >
              {error}
            </Text>
          </VStack>
        </VStack>
      </div>
    );
  }

  // 画像が読み込まれていない場合
  if (!imageUrl) {
    return (
      <div className={cn("flex items-center justify-center h-96")}>
        <Text variant="labelSmall" className="text-neutral-muted-on-fill">
          プレビューできません
        </Text>
      </div>
    );
  }

  // 画像表示
  return (
    <div
      className={cn(
        "flex items-center justify-center",
        "transition-all duration-200 ease-in-out",
      )}
    >
      <img
        src={imageUrl}
        alt={filename}
        className={cn(
          "max-w-full max-h-[calc(90vh-120px)] object-contain",
          "transition-opacity duration-200 ease-in-out",
        )}
        onError={onImageError}
        loading="lazy"
      />
    </div>
  );
}
